import { ReactNode, useState } from 'react';
import Icon, { Icons } from '../../elements/Icon';
import Tooltip from '@/presentation/components/ui/Tooltip';

export interface TableHeading {
  label: ReactNode;
  accessor?: string;
  tooltip?: ReactNode;
  align?: 'left' | 'right';
  className?: string;
}

const Table = ({
  onSort,
  header,
  headings,
  rows,
  children,
  border = true,
}: {
  header?: ReactNode;
  onSort?: (accessor: any, direction: 'asc' | 'desc') => void;
  headings: TableHeading[];
  rows?: ReactNode[][];
  children?: ReactNode;
  border?: boolean;
}) => {
  const [sortField, setSortField] = useState<string>();
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');

  const onSortClick = (h: TableHeading) => {
    if (h.accessor && onSort) {
      const sortOrder =
        h.accessor === sortField && order === 'desc' ? 'asc' : 'desc';
      setSortField(h.accessor);
      setOrder(sortOrder);
      onSort(h.accessor, sortOrder);
    }
  };

  return (
    <div
      className={`${
        border ? 'border border-gray-300 rounded-lg ' : ''
      }overflow-x-scroll hide-scroll`}
    >
      {header}
      <table className="min-w-full divide-y divide-gray-300">
        <thead className="bg-gray-100 select-none whitespace-nowrap text-xs uppercase font-bold">
          <tr>
            {headings?.map((h, i) => {
              return (
                <th
                  scope="col"
                  className={`py-2 ${
                    h.align === 'left' ? 'text-left' : 'text-right'
                  } text-gray-800 align-middle ${
                    !!h.accessor ? 'cursor-pointer pl-3 pr-1' : 'px-3'
                  } ${h.className ?? ''}`}
                  key={i}
                  onClick={() => onSortClick(h)}
                >
                  {h.tooltip ? (
                    <Tooltip
                      content={h.tooltip}
                      label={
                        <span>
                          <span
                            className={`inline-block align-middle truncate`}
                          >
                            {h.label}
                          </span>
                          <Icon
                            name={Icons.INFORMATION_CIRCLE}
                            className="inline-block ml-1"
                            size={20}
                          />
                        </span>
                      }
                    />
                  ) : (
                    <span className={`inline-block align-middle truncate`}>
                      {h.label}
                    </span>
                  )}
                  {!!h.accessor && (
                    <span className={`inline-block align-middle ml-1`}>
                      {sortField === h.accessor ? (
                        <span>
                          {order === 'desc' ? (
                            <Icon
                              name={Icons.CHEVRON_DOWN}
                              size={20}
                              className="text-gray-500"
                            />
                          ) : (
                            <Icon
                              name={Icons.CHEVRON_UP}
                              size={20}
                              className="text-gray-500"
                            />
                          )}
                        </span>
                      ) : (
                        <Icon
                          name={Icons.CHEVRON_UP_DOWN}
                          size={20}
                          className="text-gray-500"
                        />
                      )}
                    </span>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="bg-gray-50 divide-y divide-gray-300 text-sm font-medium">
          {rows
            ? rows?.map((row, i) => (
                <tr
                  key={i}
                  className={`hover:bg-pink-50/30 border-b ${
                    i === rows?.length - 1
                      ? 'border-transparent'
                      : 'border-gray-200'
                  }`}
                >
                  {row.map((cell, i) => {
                    const className =
                      i === 0
                        ? 'pl-2 pr-3'
                        : i === row.length - 1
                        ? 'relative pl-3 pr-2 text-right'
                        : 'px-3 text-right';
                    return (
                      <td
                        key={i}
                        className={`${className} py-4 whitespace-nowrap`}
                      >
                        {cell}
                      </td>
                    );
                  })}
                </tr>
              ))
            : children}
        </tbody>
      </table>
    </div>
  );
};

export default Table;
